const RE_CZECH_PHONE_NATIONAL_PREFIX = /^\+420\s*/;
const RE_WHITESPACE = /\s/g;
const RE_DIACRITICS = /[\u0300-\u036f]/g;

export const addNonBreakableSpace = (input: string): string => input.replace(RE_WHITESPACE, '\xA0');

export const trimCzechPhoneNrPrefix = (phoneNr: string): string =>
	phoneNr.replace(RE_CZECH_PHONE_NATIONAL_PREFIX, '');

export const quoteBackticks = (input: string): string => `\`${input}\``;

export const isPrintableChar = (keycode: number, includeEnterKey?: boolean): boolean =>
	keycode === 32 ||
	(includeEnterKey && keycode === 13) || // spacebar & return key(s) (if you want to allow carriage returns)
	(keycode > 47 && keycode < 58) || // number keys
	(keycode > 64 && keycode < 91) || // letter keys
	(keycode > 95 && keycode < 112) || // numpad keys
	(keycode > 185 && keycode < 193) || // ;=,-./` (in order)
	(keycode > 218 && keycode < 223); // [\]' (in order)

export const setCharAtIndex = (value: string, idx: number, char: string): string =>
	`${value.slice(0, idx)}${char}${value.slice(idx + 1)}`;

/**
 * Builds a filter that strips from a string every character that does not match `pattern`.
 * `pattern` may be a string (compiled to a global RegExp) or a RegExp used as-is.
 */
export const filterPattern = (pattern: string | RegExp): ((value: string) => string) => {
	const patternRe = typeof pattern === 'string' ? new RegExp(pattern, 'g') : pattern;
	return (value: string): string => value && (value.match(patternRe)?.join('') ?? '');
};

export function removeDiacritics(text: string): string {
	return text.normalize('NFD').replace(RE_DIACRITICS, '');
}

/**
 * Truncates the text to a maximum length if it exceeds the limit by more than 2 characters.
 * In that case, it appends an ellipsis ("...") to the end.
 *
 * Why maxLength + 2:
 * If the text is only 1-2 characters longer than maxLength, we keep it as is,
 * because adding "..." would make the result even longer than the original.
 * For example, a string with 72 characters is still shorter than 70 + "..." (which is 73),
 * so it makes sense to only truncate if the original length exceeds maxLength + 2.
 *
 * @param text - The text to potentially truncate.
 * @param maxLength - The maximum number of characters before adding the ellipsis.
 * @param ellipsis - The string to append if truncation happens.
 * @returns The original or truncated string with an ellipsis.
 */
export function truncateWithEllipsis(text: string, maxLength: number, ellipsis = '...'): string {
	const totalAllowedLength = maxLength + ellipsis.length - 1;
	if (text.length > totalAllowedLength) {
		return text.slice(0, maxLength) + ellipsis;
	}
	return text;
}

export function asNonEmptyString(value: unknown): string | undefined {
	if (typeof value === 'string') {
		return value || undefined;
	}
	if (typeof value === 'number') {
		return String(value);
	}
	return undefined;
}
